import { apiRequest, apiUpload } from './client'
import { readInt, readString } from './json'
import type {
  CatalogImportConfirmResultDto,
  CatalogImportPreviewDto,
  CatalogImportPreviewRowsDto,
  CatalogImportRowFieldOverride,
  ColumnMapping,
  ImportColumnDto,
  ImportPreviewRowDto,
  MatchCandidateDto,
  MatchPreviewDto,
  ProposedMappingDto,
} from './catalogImportTypes'
import { mappingPayload } from './catalogImportTypes'

const API_PREFIX = '/buyer/private-catalog/import'

type Raw = Record<string, unknown>

const normalizeColumn = (json: Raw): ImportColumnDto => ({
  index: Number(json.index ?? json.columnIndex ?? 0),
  header: readString(json, 'header', 'columnHeader') ?? '',
  sampleValues: ((json.sampleValues as unknown[] | undefined) ?? []).map(String),
})

const normalizeProposedMapping = (json: Raw): ProposedMappingDto => ({
  field: (json.field as string) ?? '',
  columnIndex:
    json.columnIndex != null
      ? Number(json.columnIndex)
      : json.column_index != null
        ? Number(json.column_index)
        : null,
  columnHeader: readString(json, 'columnHeader', 'column_header'),
  confidence: json.confidence != null ? Number(json.confidence) : null,
  source: (json.source as string | null) ?? null,
  required: (json.required as boolean) ?? false,
})

const normalizeCandidate = (json: Raw): MatchCandidateDto => ({
  productTypeId: readInt(json, 'productTypeId', 'product_type_id'),
  code: readString(json, 'code', 'code') ?? '',
  name: readString(json, 'name', 'name') ?? '',
  score: Number(json.score ?? 0),
})

const normalizeMatchPreview = (json: Raw): MatchPreviewDto => ({
  matcherVersion: readString(json, 'matcherVersion', 'matcher_version') ?? '',
  decision: readString(json, 'decision', 'decision') ?? 'unmatched',
  candidates: ((json.candidates as Raw[] | undefined) ?? []).map(normalizeCandidate),
})

const normalizePreviewRow = (json: Raw): ImportPreviewRowDto => ({
  rowNumber: readInt(json, 'rowNumber', 'row_number'),
  name: readString(json, 'name', 'name') ?? '',
  externalSku: readString(json, 'externalSku', 'external_sku'),
  brand: (json.brand as string | null) ?? null,
  country: (json.country as string | null) ?? null,
  unit: (json.unit as string | null) ?? null,
  price: json.price != null ? String(json.price) : null,
  minShipmentQty:
    json.minShipmentQty != null
      ? String(json.minShipmentQty)
      : json.min_shipment_qty != null
        ? String(json.min_shipment_qty)
        : null,
  packageSize: readString(json, 'packageSize', 'package_size'),
  category: (json.category as string | null) ?? null,
  matchPreview: normalizeMatchPreview((json.matchPreview ?? json.match_preview) as Raw),
})

const normalizePreview = (json: Raw): CatalogImportPreviewDto => ({
  previewToken: readString(json, 'previewToken', 'preview_token') ?? '',
  organizationId: readInt(json, 'organizationId', 'organization_id'),
  filename: readString(json, 'filename', 'filename') ?? '',
  columns: ((json.columns as Raw[] | undefined) ?? []).map(normalizeColumn),
  proposedMapping: ((json.proposedMapping as Raw[] | undefined) ?? []).map(
    normalizeProposedMapping,
  ),
  sampleRows: ((json.sampleRows as Raw[] | undefined) ?? []).map(normalizePreviewRow),
  totalRows: Number(json.totalRows ?? json.total_rows ?? 0),
  warnings: ((json.warnings as unknown[] | undefined) ?? []).map(String),
  savedProfileApplied: (json.savedProfileApplied as boolean) ?? false,
  expiresAt: readString(json, 'expiresAt', 'expires_at'),
})

const normalizePreviewRows = (json: Raw): CatalogImportPreviewRowsDto => ({
  rows: ((json.rows as Raw[] | undefined) ?? []).map(normalizePreviewRow),
  offset: Number(json.offset ?? 0),
  limit: Number(json.limit ?? 0),
  totalRows: Number(json.totalRows ?? json.total_rows ?? 0),
  hasMore: (json.hasMore as boolean) ?? false,
  expiresAt: readString(json, 'expiresAt', 'expires_at'),
})

const normalizeConfirmResult = (json: Raw): CatalogImportConfirmResultDto => ({
  imported: Number(json.imported ?? 0),
  autoMatched: Number(json.autoMatched ?? json.auto_matched ?? 0),
  suggested: Number(json.suggested ?? 0),
  unmatched: Number(json.unmatched ?? 0),
  updatedExisting: Number(json.updatedExisting ?? json.updated_existing ?? 0),
})

export const importCatalogPreview = async (
  organizationId: number,
  file: File,
): Promise<CatalogImportPreviewDto> => {
  const formData = new FormData()
  formData.append('file', file)
  const result = await apiUpload<Raw>(
    `${API_PREFIX}/preview?organizationId=${organizationId}`,
    formData,
  )
  return normalizePreview(result)
}

export const importCatalogPreviewRows = async (options: {
  organizationId: number
  previewToken: string
  columnMapping: ColumnMapping
  offset: number
  limit?: number
}): Promise<CatalogImportPreviewRowsDto> => {
  const result = await apiRequest<Raw>(`${API_PREFIX}/preview/rows`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      preview_token: options.previewToken,
      column_mapping: mappingPayload(options.columnMapping),
      offset: options.offset,
      limit: options.limit ?? 50,
    },
  })
  return normalizePreviewRows(result)
}

export const confirmCatalogImport = async (options: {
  organizationId: number
  venueId?: number | null
  previewToken: string
  columnMapping: ColumnMapping
  rowProductTypeOverrides?: Record<number, number>
  rowFieldOverrides?: Record<number, CatalogImportRowFieldOverride>
  excludedRowNumbers?: number[]
  privateSupplierId?: number | null
  newSupplier?: {
    legalName: string
    inn: string
    displayLabel?: string | null
  } | null
}): Promise<CatalogImportConfirmResultDto> => {
  const rowProductTypeOverrides: Record<string, number> = {}
  for (const [row, typeId] of Object.entries(options.rowProductTypeOverrides ?? {})) {
    rowProductTypeOverrides[row] = typeId
  }

  const rowFieldOverrides: Record<string, Record<string, string>> = {}
  for (const [row, override] of Object.entries(options.rowFieldOverrides ?? {})) {
    const payload: Record<string, string> = {}
    if (override.name != null) payload.name = override.name
    if (override.brand != null) payload.brand = override.brand
    if (override.price != null) payload.price = override.price
    if (override.country != null) payload.country = override.country
    if (override.unit != null) payload.unit = override.unit
    if (override.minShipmentQty != null) payload.min_shipment_qty = override.minShipmentQty
    if (override.packageSize != null) payload.package_size = override.packageSize
    if (override.comment != null) payload.comment = override.comment
    if (Object.keys(payload).length > 0) {
      rowFieldOverrides[row] = payload
    }
  }

  const result = await apiRequest<Raw>(`${API_PREFIX}/confirm`, {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      ...(options.venueId != null ? { venue_id: options.venueId } : {}),
      preview_token: options.previewToken,
      column_mapping: mappingPayload(options.columnMapping),
      ...(Object.keys(rowProductTypeOverrides).length > 0
        ? { row_product_type_overrides: rowProductTypeOverrides }
        : {}),
      ...(Object.keys(rowFieldOverrides).length > 0
        ? { row_field_overrides: rowFieldOverrides }
        : {}),
      ...(options.excludedRowNumbers && options.excludedRowNumbers.length > 0
        ? { excluded_row_numbers: options.excludedRowNumbers }
        : {}),
      ...(options.privateSupplierId != null
        ? { private_supplier_id: options.privateSupplierId }
        : {}),
      ...(options.newSupplier
        ? {
            new_supplier: {
              legal_name: options.newSupplier.legalName,
              inn: options.newSupplier.inn,
              ...(options.newSupplier.displayLabel?.trim()
                ? { display_label: options.newSupplier.displayLabel.trim() }
                : {}),
            },
          }
        : {}),
    },
  })
  return normalizeConfirmResult(result)
}
